import React, { useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Link,
  Divider,
} from "@mui/material";
import { useWhitelabel } from "../context/WhitelabelContext";
import { getInstanceConfig, getLogoMode, getSupportContactInfo } from "../utils";
import { useNavigate } from "react-router-dom";
import BrandedPageFooter from "../components/BrandedPageFooter";

interface PrivacyPolicyProps {
  mode: string;
}

const PrivacyPolicy: React.FC<PrivacyPolicyProps> = ({ mode }) => {
  const navigate = useNavigate();
  const { whitelabelSettings, whitelabelLogos, loaded } = useWhitelabel();
  const instanceConfig = getInstanceConfig(mode, whitelabelSettings);

  // Check if a URL would cause a redirect loop by pointing back to our privacy page
  const wouldCauseRedirectLoop = (url: string): boolean => {
    try {
      const targetUrl = new URL(url, window.location.origin);
      const currentHost = window.location.hostname.toLowerCase();
      const targetHost = targetUrl.hostname.toLowerCase();
      const targetPath = targetUrl.pathname.toLowerCase();

      // Check if the URL points to a privacy page on the same domain
      if (targetHost === currentHost) {
        // Check for common privacy page paths
        if (targetPath === '/privacy' || targetPath === '/privacy/' ||
            targetPath === '/privacy-policy' || targetPath === '/privacy-policy/') {
          return true;
        }
      }
      return false;
    } catch {
      // If URL parsing fails, don't redirect to be safe
      return true;
    }
  };

  // Redirect to external privacy URL if configured (unless it would cause a loop)
  const privacyUrl = whitelabelSettings?.privacyUrl;
  const shouldRedirectToExternal = privacyUrl && !wouldCauseRedirectLoop(privacyUrl);

  useEffect(() => {
    if (!loaded) return;

    if (shouldRedirectToExternal) {
      window.location.href = privacyUrl;
    }
  }, [loaded, shouldRedirectToExternal, privacyUrl]);

  // Show blank page while loading or redirecting
  if (!loaded || shouldRedirectToExternal) {
    return <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }} />;
  }

  const getLogoUrl = () => {
    if (mode === "dark" && whitelabelLogos?.horizontal_logo_dark?.url) {
      return whitelabelLogos.horizontal_logo_dark.url;
    }
    if (whitelabelLogos?.horizontal_logo?.url) {
      return whitelabelLogos.horizontal_logo.url;
    }
    return getLogoMode(mode);
  };

  const appName = whitelabelSettings?.title || "GTM AI";
  const companyName = appName;
  const supportContact = getSupportContactInfo(whitelabelSettings);
  const privacyEmail = whitelabelSettings?.securityEmail || whitelabelSettings?.ownerEmail || whitelabelSettings?.salesEmail || "privacy@" + window.location.hostname;
  const effectiveDate = "December 11, 2025";

  const Section: React.FC<{
    title: string;
    children: React.ReactNode;
  }> = ({ title, children }) => (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
        {title}
      </Typography>
      {children}
    </Box>
  );

  const BulletList: React.FC<{ items: string[] }> = ({ items }) => (
    <Box component="ul" sx={{ pl: 3, my: 1 }}>
      {items.map((item, idx) => (
        <Typography
          key={idx}
          component="li"
          variant="body2"
          color="text.secondary"
          sx={{ mb: 0.5 }}
        >
          {item}
        </Typography>
      ))}
    </Box>
  );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "background.default",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          bgcolor: "background.default",
          borderBottom: 1,
          borderColor: "divider",
          py: 6,
        }}
      >
        <Container maxWidth="md" sx={{ textAlign: "center" }}>
          <Box
            component="img"
            src={getLogoUrl()}
            alt={appName}
            sx={{
              height: 48,
              mb: 3,
              objectFit: "contain",
              cursor: "pointer",
            }}
            onClick={() => navigate("/")}
          />
          <Typography
            variant="h3"
            component="h1"
            gutterBottom
            sx={{ color: instanceConfig.primaryColor }}
          >
            Privacy Policy
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600, mx: "auto" }}>
            This Privacy Policy describes how {companyName} ("{appName}", "we", "us", or "our")
            collects, uses, and shares your personal information when you use our services.
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
            Effective Date: {effectiveDate}
          </Typography>
        </Container>
      </Box>

      {/* Main Content */}
      <Container maxWidth="md" sx={{ py: 6, flex: 1 }}>
        <Typography variant="body1" color="text.secondary" paragraph>
          At {appName}, we take your privacy seriously. This Privacy Policy explains what information
          we collect, how we use it, when we may share it, and how you can control your information.
          By using our services, you agree to the collection and use of information in accordance
          with this policy.
        </Typography>

        <Divider sx={{ my: 4 }} />

        <Section title="1. Information We Collect">
          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
            Information You Provide to Us:
          </Typography>
          <BulletList
            items={[
              "Account information (name, email address, password)",
              "Profile information (company name, job title, profile photo)",
              "Payment and billing information (processed securely via third-party payment processors)",
              "Content you create, upload, or share through our services",
              "Communications with us (support requests, feedback, surveys)",
              "Information from integrations you connect (Google Drive, Notion, etc.)",
            ]}
          />

          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
            Information We Collect Automatically:
          </Typography>
          <BulletList
            items={[
              "Device information (browser type, operating system, device identifiers)",
              "Usage information (features used, pages visited, actions taken)",
              "Log data (IP address, access times, referring URLs)",
              "Cookies and similar tracking technologies",
              "Performance and error data to improve our services",
            ]}
          />

          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
            Information from Third Parties:
          </Typography>
          <BulletList
            items={[
              "Information from identity providers when you use single sign-on (SSO)",
              "Information from connected integrations with your authorization",
              "Publicly available business information for lead generation features",
            ]}
          />
        </Section>

        <Section title="2. How We Use Your Information">
          <Typography variant="body2" color="text.secondary" paragraph>
            We use the information we collect for the following purposes:
          </Typography>
          <BulletList
            items={[
              "Provide, maintain, and improve our services",
              "Process transactions and send related information",
              "Send you technical notices, updates, and support messages",
              "Respond to your comments, questions, and customer service requests",
              "Communicate with you about products, services, and events",
              "Monitor and analyze trends, usage, and activities",
              "Detect, investigate, and prevent security incidents and fraudulent transactions",
              "Personalize and improve your experience",
              "Comply with legal obligations",
            ]}
          />

          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
            AI Processing:
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Our services use artificial intelligence to process your content and data. We do not
            use your data to train AI models unless you explicitly opt-in. Your content is
            processed in real-time and we implement zero data retention policies with our AI
            providers where available.
          </Typography>
        </Section>

        <Section title="3. How We Share Your Information">
          <Typography variant="body2" color="text.secondary" paragraph>
            We may share your information in the following circumstances:
          </Typography>
          <BulletList
            items={[
              "With service providers who perform services on our behalf (hosting, payment processing, analytics)",
              "With AI model providers to process your requests (subject to strict data processing agreements)",
              "With your consent or at your direction (e.g., when connecting integrations)",
              "To comply with legal obligations or respond to lawful requests",
              "To protect our rights, privacy, safety, or property, or that of our users",
              "In connection with a merger, acquisition, or sale of assets",
              "With affiliated companies under common ownership or control",
            ]}
          />

          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            <strong>We do not sell your personal information</strong> to third parties for their
            marketing purposes.
          </Typography>
        </Section>

        <Section title="4. Data Retention">
          <Typography variant="body2" color="text.secondary" paragraph>
            We retain your information for as long as your account is active or as needed to
            provide you services. We will also retain and use your information as necessary to:
          </Typography>
          <BulletList
            items={[
              "Comply with our legal obligations",
              "Resolve disputes and enforce our agreements",
              "Maintain business records for reasonable periods",
              "Protect against fraudulent or illegal activity",
            ]}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            You may request deletion of your personal data at any time by contacting us.
            Upon account deletion, we will delete or anonymize your data within 30 days,
            except where retention is required by law.
          </Typography>
        </Section>

        <Section title="5. Data Security">
          <Typography variant="body2" color="text.secondary" paragraph>
            We implement appropriate technical and organizational measures to protect your
            personal information, including:
          </Typography>
          <BulletList
            items={[
              "Encryption at rest (AES-256) and in transit (TLS 1.2+)",
              "Regular security assessments and penetration testing",
              "Access controls and authentication requirements",
              "Audit logging of system access",
              "Employee security training and background checks",
              "Incident response procedures",
            ]}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            While we strive to protect your personal information, no method of transmission over
            the Internet or electronic storage is 100% secure. We cannot guarantee absolute security.
          </Typography>
        </Section>

        <Section title="6. Cookies and Tracking Technologies">
          <Typography variant="body2" color="text.secondary" paragraph>
            We use cookies and similar tracking technologies to collect and track information
            about your use of our services. Types of cookies we use include:
          </Typography>
          <BulletList
            items={[
              "Essential cookies: Required for the operation of our services",
              "Functional cookies: Remember your preferences and settings",
              "Analytics cookies: Help us understand how our services are used",
              "Marketing cookies: Used to deliver relevant advertisements (with your consent)",
            ]}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            You can control cookies through your browser settings. Note that disabling certain
            cookies may affect the functionality of our services.
          </Typography>
        </Section>

        <Section title="7. Your Rights and Choices">
          <Typography variant="body2" color="text.secondary" paragraph>
            Depending on your location, you may have the following rights regarding your personal data:
          </Typography>
          <BulletList
            items={[
              "Access: Request a copy of your personal information",
              "Correction: Request correction of inaccurate or incomplete data",
              "Deletion: Request deletion of your personal information",
              "Portability: Request a copy of your data in a portable format",
              "Restriction: Request restriction of processing of your data",
              "Objection: Object to processing of your data for certain purposes",
              "Withdraw consent: Where processing is based on consent",
              "Opt-out of sale/sharing: We do not sell your data, but you can opt-out of data sharing",
            ]}
          />

          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
            GDPR (European Economic Area):
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            If you are in the EEA, you have additional rights under the General Data Protection
            Regulation. Our legal bases for processing include performance of a contract,
            legitimate interests, legal obligations, and consent where applicable.
          </Typography>

          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
            CCPA (California):
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            California residents have specific rights under the California Consumer Privacy Act,
            including the right to know, delete, and opt-out. We do not sell personal information
            as defined by the CCPA.
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            To exercise any of these rights, please contact us at{" "}
            <Link href={`mailto:${privacyEmail}`}>{privacyEmail}</Link>. We will respond to
            your request within the legally required timeframe.
          </Typography>
        </Section>

        <Section title="8. International Data Transfers">
          <Typography variant="body2" color="text.secondary" paragraph>
            Your information may be transferred to and processed in countries other than your
            country of residence. These countries may have data protection laws that are different
            from the laws of your country.
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            When we transfer data internationally, we implement appropriate safeguards such as:
          </Typography>
          <BulletList
            items={[
              "Standard contractual clauses approved by relevant authorities",
              "Data processing agreements with our service providers",
              "Compliance with applicable cross-border transfer mechanisms",
            ]}
          />
        </Section>

        <Section title="9. Children's Privacy">
          <Typography variant="body2" color="text.secondary" paragraph>
            Our services are not directed to children under 16 years of age (or the applicable age
            of consent in your jurisdiction). We do not knowingly collect personal information from
            children. If we learn that we have collected personal information from a child, we will
            take steps to delete that information promptly.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            If you believe we have inadvertently collected information from a child, please contact
            us at <Link href={`mailto:${privacyEmail}`}>{privacyEmail}</Link>.
          </Typography>
        </Section>

        <Section title="10. Third-Party Links and Services">
          <Typography variant="body2" color="text.secondary" paragraph>
            Our services may contain links to third-party websites, applications, or services.
            This Privacy Policy does not apply to those third-party services. We encourage you
            to review the privacy policies of any third-party services you access.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            When you connect third-party integrations to our services, those integrations are
            governed by the respective third party's terms and privacy policies.
          </Typography>
        </Section>

        <Section title="11. Changes to This Privacy Policy">
          <Typography variant="body2" color="text.secondary" paragraph>
            We may update this Privacy Policy from time to time. We will notify you of any changes by:
          </Typography>
          <BulletList
            items={[
              "Posting the new Privacy Policy on this page",
              "Updating the 'Effective Date' at the top of this policy",
              "Sending an email notification for material changes (where required)",
            ]}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            We encourage you to review this Privacy Policy periodically. Your continued use of
            our services after any changes constitutes acceptance of the updated policy.
          </Typography>
        </Section>

        <Section title="12. Contact Us">
          <Typography variant="body2" color="text.secondary" paragraph>
            If you have any questions about this Privacy Policy or our data practices, please
            contact us:
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Email:</strong>{" "}
            <Link href={`mailto:${privacyEmail}`}>{privacyEmail}</Link>
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            For general support inquiries, please visit our{" "}
            <Link
              href={supportContact}
              target={supportContact.startsWith("mailto:") ? undefined : "_blank"}
              rel={supportContact.startsWith("mailto:") ? undefined : "noopener noreferrer"}
            >
              support page
            </Link>
            .
          </Typography>
        </Section>
      </Container>

      <BrandedPageFooter currentPage="privacy" />
    </Box>
  );
};

export default PrivacyPolicy;
